import React, { Component } from 'react';
import { Route, withRouter } from 'react-router-dom';
import NotFound from '@Pages/NotFound';
import AverageSalary from '@Components/AverageSalary';
import SalaryComparison from '@Components/SalaryComparison';
import SearchForm from '@Components/SearchForm';
import JobAlertsSection from '@Components/JobAlertsSection';
import SearchStickyMobile from '@Components/SearchStickyMobile';
import NoResults from '@Components/NoResults';
import RelatedJobs from '@Components/RelatedJobs';
import LanguageToast from '@Components/LanguageToast';
import Breadcrumbs from '@UI/Breadcrumbs';
import getRouteFromUrl from 'getRouteFromUrl';
import { isMultilingual } from 'getOpCoRoutes';
import { injectIntl } from 'react-intl';
import { getSearchParams } from '@Utils/searchParams';
import getCallback from 'getCallback';
import SeoMetadata from '@Components/SeoMetadata';
import { List } from 'react-content-loader';
import { Helmet } from 'rnd-helmet';
import { dataLayerSalaryCheckerPageView, pushDataLayerSalaryInfo } from 'datalayer';
import getCountryName from 'getCountryName';
import textTransform from 'textTransform';

class SalaryChecker extends Component {
  constructor(props) {
    super(props);
    let params;
    const { staticContext } = props;

    try {
      params = getSearchParams(props.match.params.searchParams, props.location.hash);
    } catch (e) {
      staticContext.status = 404;
      return;
    }
    let initialData = params;
    if (staticContext && staticContext?.data) {
      if (staticContext.data.statusCode === 404) {
        staticContext.status = 404;
        return;
      }
      initialData = { ...staticContext.data, grossSalary: params.salary };
      staticContext.dataLayer = dataLayerSalaryCheckerPageView(props.intl.locale);
    } else if (typeof window !== 'undefined' && window.__ROUTE_DATA__) {
      initialData = { ...window.__ROUTE_DATA__, grossSalary: params.salary };
      this.pushDataLayerSalaryInfo(initialData);
    }
    this.state = {
      ...initialData,
      contentLoaded: typeof initialData.count !== 'undefined',
      grossSalary: params.salary,
    };
  }

  /** @inheritdoc */
  componentDidUpdate(prevProps) {
    const { props } = this;
    const newParams = getSearchParams(props.match.params.searchParams, props.location.hash);
    const prevParams = getSearchParams(prevProps.match.params.searchParams, prevProps.location.hash);
    if (newParams.salary !== prevParams.salary) {
      this.setState((prev) => ({ ...prev, grossSalary: newParams.salary }));
    }

    if (prevProps.match.params.searchParams !== props.match.params.searchParams) {
      this.setState((prev) => ({ ...prev, contentLoaded: false }));
      getCallback({ currentRoute: props.match, locale: props.intl.locale, data: newParams })
        .then((response) => {
          this.setState({ ...response.data, contentLoaded: true });
          this.pushDataLayerSalaryInfo(response.data, true);
          return response.data;
        }).catch(() => {
          this.setState((prev) => ({ ...prev, contentLoaded: true }));
          return [];
        });
    } else if (newParams.salary !== prevParams.salary) {
      // as setState is async function state is without updated gross salary info.
      this.pushDataLayerSalaryInfo({ ...this.state, grossSalary: newParams.salary }, true);
    }
  }

  /**
   * Push datalayer events related to the salary results page.
   *
   * @param {Object} stateData
   *   An actual state of the page.
   * @param {Bool} pageView
   *  True in case page url has changed and salary recalculated.
   */
  pushDataLayerSalaryInfo = (stateData, pageView = false) => {
    window.dataLayer = window.dataLayer || [];
    if (pageView) {
      // Push the dataLayer as new array item.
      window.dataLayer.push({
        event: 'pageview',
        virtualPage: this.props.location.pathname + this.props.location.hash,
        ...dataLayerSalaryCheckerPageView(this.props.intl.locale),
      });
    }
    const location = stateData.city && stateData.region ? `${stateData.city}, ${stateData.region}` : '';
    pushDataLayerSalaryInfo(
      stateData.occupation.job_title,
      location,
      parseInt(stateData.grossSalary, 10),
      stateData.salary,
    );
  };

  /**
   * Generate page title based on the provided seo overrides.
   *
   * @returns {string}
   *   Content for the <h1> page tag.
   */
  getPageTitle = () => {
    if (this.state.seoOverrides?.pageTittle) {
      return this.state.seoOverrides.pageTittle
        .replace('[occupation]', textTransform(this.state.occupation.job_title))
        .replace('[city]', textTransform(this.state.city || ''))
        .replace('[region]', textTransform(this.state.region || ''));
    }
    return this.props.intl.formatMessage({ id: 'Salary.MainTitle' }, {
      occupation: textTransform(this.state.occupation.job_title),
      location: this.state.city && this.state.region ? textTransform(`${this.state.city}, ${this.state.region}`) : textTransform(getCountryName(this.props.intl.locale)),
    });
  };

  /**
   * Get breadcrumbs for the page.
   *
   * @returns {{items: [{title: {String}, url: {String}}], mobileItem: [{title: {String}, url: {String}}]}}
   *   Object with last breadcrumb and the list of breadcrumbs.
   */
  getBreadcrumbs = () => {
    const routeConfig = getRouteFromUrl(this.props.intl.locale, this.props.match.path);
    const items = routeConfig?.breadcrumbs?.items || [];

    // Remove dot from the breadcrumb to follow branding compliance.
    let crumbTitle = this.getPageTitle();
    if (crumbTitle[crumbTitle.length - 1] === '.') {
      crumbTitle = crumbTitle.slice(0, -1);
    }
    return {
      mobileItem: items[items.length - 1] || null,
      items: [...items, { title: crumbTitle, url: null }],
    };
  };

  // Unable to fix complexity in the render.
  /* eslint-disable-next-line sonarjs/cognitive-complexity */
  render() {
    const { staticContext } = this.props;
    if (staticContext && staticContext.status === 404) {
      return (
        <Route render={() => (<NotFound staticContext={staticContext} />)} />
      );
    }
    const { match } = this.props;

    // Need to add a coma and space before region if it exists.
    const { state } = this;
    const { contentLoaded } = this.state;
    const searchParams = {
      city: state.city,
      region: state.region,
      salary: state.grossSalary,
      occupation: state.occupation.job_title,
    };

    const breadcrumbs = this.getBreadcrumbs();
    const backgroundColor = process.env.REACT_APP_BRAND_GUIDELINES === 'true' ? 'bg-variant-brand-primary' : 'bg-variant-brand-secondary';

    return (
      <div className="salary-page">
        <div className={`header header--search-alternative mvp-header--search-alternative header--text ${backgroundColor}`}>
          <Breadcrumbs {...breadcrumbs} bgColor={process.env.REACT_APP_BRAND_GUIDELINES === 'true' ? 'primary' : 'secondary'} />
          <SearchForm
            baseUrl={match.path.replace(':searchParams*', '')}
            searchParams={searchParams}
            averageSalary={state.salary}
          />
        </div>

        <SearchStickyMobile searchParams={searchParams} />

        <section className="salary-section wrapper bg-brand--white">
          <h1 className="salary-section__title">{this.getPageTitle()}</h1>
          <div className="block__wrapper block__salary l:flex">
            <div className="block__salary__wrapper__average">
              { !contentLoaded && <List /> }
              { contentLoaded && state.count > 0 && <AverageSalary salary={state.salary} count={state.count} /> }
            </div>
            <div className="block__salary__wrapper__annual">
              { !contentLoaded && <List /> }
              { contentLoaded && state.count > 0 && <SalaryComparison searchParams={searchParams} salary={state.salary} /> }
            </div>
          </div>
          { contentLoaded && state.count <= 0 && <NoResults searchParams={searchParams} /> }
        </section>
        {process.env.REACT_APP_COUNTRY !== 'us' ? (
          <RelatedJobs searchParams={searchParams} salary={state.salary} />
        ) : null}
        <JobAlertsSection
          baseUrl={match.path.replace(':searchParams*', '')}
          searchParams={searchParams}
        />
        {isMultilingual() ? (
          <LanguageToast />
        ) : null}
        {!!state.seoOverrides?.isAvailable && contentLoaded && state.count > 0 && (
          <div data-testid="seo-overrides">
            <Helmet>
              <script type="text/javascript" src={`${process.env.REACT_APP_ASSETS_BASE_PATH}/js/lazy-load.js`} />
            </Helmet>
            {/* todo check how to fix it. */}
            {/* eslint-disable-next-line xss/no-mixed-html,react/no-danger */}
            <div dangerouslySetInnerHTML={{ __html: state.seoOverrides.content }} />
          </div>
        )}
        { contentLoaded && <SeoMetadata {...state} /> }
      </div>
    );
  }
}

export default injectIntl(withRouter(SalaryChecker));
